import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import {
  Button,
  makeStyles,
  shorthands,
  tokens,
  Toaster,
  useToastController,
  Toast,
  ToastTitle, mergeClasses
} from "@fluentui/react-components";
import {
  ArrowLeft20Regular,
  DocumentAdd20Regular,
  VehicleCarProfile20Regular,
} from "@fluentui/react-icons";
import { apiUrl } from "../utils/api";
import { BRAND } from "../components/risikoanalyseStep/tokens";
import { sectionColorHex } from "../components/risikoanalyseStep/sectionColor";
import BottomBar from "../components/risikoanalyseStep/BottomBar";
import StepHero from "../components/kfzContractStep/StepHero";
import { KFZ_CONTRACT_SECTIONS } from "../components/kfzContractStep/sections";
import VertragsBasisSection, {
  type VersichererKey,
} from "../components/kfzContractStep/VertragsBasisSection";
import RisikoAuswahlSection from "../components/kfzContractStep/RisikoAuswahlSection";
import RisikoinfoSection from "../components/kfzContractStep/RisikoinfoSection";
import GefahrenSection from "../components/kfzContractStep/GefahrenSection";
import ZahlungSection from "../components/kfzContractStep/ZahlungSection";
import SammelvertragGrid, {
  initialSammelGrid,
  countSelections,
  type SammelGridState,
} from "../components/kfzContractStep/SammelvertragGrid";
import VereinbarungFileUpload from "../components/kfzContractStep/VereinbarungFileUpload";
import { getVereinbarungMock } from "../components/kfzContractStep/vereinbarungMock";
import UmfangAssignmentTable from "../components/kfzContractStep/UmfangAssignmentTable";
import SammelvertragZahlungSummary from "../components/kfzContractStep/SammelvertragZahlungSummary";
import {
  KLASSEN_LABELS,
  TARIFIERUNGSARTEN,
  VEHICLE_CLASS_TO_KEY,
  VERTRAGS_SECTIONS,
  VERTRAGSARTEN,
} from "../components/kfzContractStep/data";
import VehicleClassPicker from "../components/risikoKfzStep/VehicleClassPicker";
import { VEHICLE_CLASSES } from "../components/risikoKfzStep/vehicleClasses";
import { ContactSelector } from "../components/ContactSelector";
import type {
  ContactSummary,
  ContractStepKind,
  GefahrenAnswers,
  KlasseKey,
  RiskSummary,
  TarifierungKey,
  VertragsAnswers,
  VertragsartKey,
  VorvertragInfo,
} from "../components/kfzContractStep/types";

const DRAFT_LS_PREFIX = "kfz-contract-draft:";
const AUTOSAVE_DEBOUNCE_MS = 1500;

const useStyles = makeStyles({
  page: {
    paddingTop: "0",
    paddingBottom: "0",
    paddingLeft: "24px",
    paddingRight: "24px",
    maxWidth: "1200px",
    marginLeft: "auto",
    marginRight: "auto",
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
    fontFamily: BRAND.fontPrimary,
    color: BRAND.ink,
    minHeight: "100%",
  },
  stickyHeader: {
    position: "sticky",
    top: 0,
    zIndex: 10,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "16px",
    paddingRight: "16px",
    backgroundColor: BRAND.white,
    display: "flex",
    flexDirection: "column",
    rowGap: "6px",
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius("12px"),
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    marginBottom: "12px",
  },
  stickyFooter: {
    position: "sticky",
    bottom: 0,
    zIndex: 10,
    marginTop: "auto",
    marginLeft: "-24px",
    marginRight: "-24px",
    backgroundColor: BRAND.white,
    ...shorthands.borderTop("1px", "solid", "rgba(45,20,43,0.1)"),
    boxShadow: "0 -6px 18px rgba(45,20,43,0.06)",
  },
  pageHeader: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    flexWrap: "wrap",
  },
  pageTitle: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightBold,
    color: BRAND.ink,
    margin: 0,
    display: "inline-flex",
    alignItems: "center",
    columnGap: "4px",
    flexWrap: "wrap",
  },
  titleStack: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    columnGap: "8px",
    flexWrap: "wrap",
    minWidth: 0,
    flexGrow: 1,
  },
  titleChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
    alignItems: "center",
  },
  titleChip: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "4px",
    paddingTop: "2px",
    paddingBottom: "2px",
    paddingLeft: "8px",
    paddingRight: "8px",
    backgroundColor: tokens.colorNeutralBackground2,
    color: BRAND.ink,
    fontSize: "0.72rem",
    fontWeight: 600,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius("999px"),
    lineHeight: 1.2,
  },
  titleChipIcon: {
    display: "inline-flex",
    alignItems: "center",
    color: BRAND.muted,
  },
  contextPanel: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
    alignItems: "flex-start",
  },
  contextBlock: {
    display: "inline-flex",
    alignItems: "baseline",
    columnGap: "8px",
    minWidth: 0,
    flexWrap: "wrap",
  },
  contextLabel: {
    fontSize: "0.95rem",
    fontWeight: 700,
    color: BRAND.muted,
  },
  contextName: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "1.1rem",
    fontWeight: 700,
    color: BRAND.ink,
  },
  contextSub: {
    fontSize: "0.95rem",
    color: BRAND.muted,
  },
  contextRisks: {
    flexGrow: 1,
    minWidth: 0,
  },
  contextRiskList: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "inline-flex",
    flexWrap: "wrap",
    columnGap: "12px",
    rowGap: "2px",
  },
  contextRiskItem: {
    display: "inline-flex",
    alignItems: "baseline",
    columnGap: "6px",
  },
  contextRiskPlate: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "1.1rem",
    color: BRAND.ink,
    fontWeight: 700,
  },
  contextRiskId: {
    fontSize: "0.85rem",
    color: BRAND.muted,
  },
  formPanel: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusLarge),
    paddingTop: "20px",
    paddingBottom: "20px",
    paddingLeft: "22px",
    paddingRight: "22px",
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
  },
  sectionPill: {
    display: "inline-block",
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "10px",
    paddingRight: "10px",
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: BRAND.white,
    alignSelf: "flex-start",
    ...shorthands.borderRadius("999px"),
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "16px",
    flexWrap: "wrap",
    marginTop: "4px",
  },
  heroBox: { flexShrink: 0 },
  title: {
    margin: 0,
    fontFamily: BRAND.fontAccent,
    fontSize: "clamp(1.6rem, 4vw, 2.4rem)",
    fontWeight: 400,
    color: BRAND.ink,
    lineHeight: 1.15,
    flexGrow: 1,
    minWidth: "200px",
  },
  description: {
    marginTop: "0",
    marginBottom: 0,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.muted,
  },
  questions: { marginTop: "12px" },
  loadingState: {
    paddingTop: "60px",
    paddingBottom: "60px",
    textAlign: "center",
    color: BRAND.muted,
    fontSize: "1rem",
  },
  errorState: {
    paddingTop: "60px",
    paddingBottom: "60px",
    textAlign: "center",
    color: "#a1331c",
    fontSize: "1rem",
  },
  sectionTabs: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: "6px",
    rowGap: "6px",
  },
  sectionTab: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: tokens.colorNeutralBackground1,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    fontWeight: 600,
    paddingTop: "5px",
    paddingBottom: "5px",
    paddingLeft: "10px",
    paddingRight: "10px",
    ...shorthands.borderRadius("999px"),
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    transitionProperty: "background-color, border-color, color",
    transitionDuration: "120ms",
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
    },
  },
  sectionTabActive: {
    color: BRAND.white,
  },
  klasseRow: {
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
  },
  klasseHeader: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
  },
  klasseHeaderLabel: {
    fontFamily: BRAND.fontPrimary,
    fontWeight: 600,
    color: BRAND.ink,
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "8px",
  },
  summaryRow: {
    display: "grid",
    gridTemplateColumns: "1fr",
    columnGap: "16px",
    rowGap: "2px",
    paddingTop: "6px",
    paddingBottom: "6px",
    ...shorthands.borderBottom("1px", "dashed", BRAND.borderDashed),
    "@media (min-width: 720px)": {
      gridTemplateColumns: "minmax(220px, 0.8fr) 1.2fr",
    },
  },
  summaryKey: {
    fontSize: "0.8rem",
    fontWeight: 600,
    color: BRAND.muted,
  },
  summaryValue: {
    fontSize: "0.95rem",
    color: BRAND.ink,
    whiteSpace: "pre-wrap",
  },
  riskTabsRow: {
    display: "flex",
    columnGap: "8px",
    rowGap: "8px",
    flexWrap: "wrap",
    marginBottom: "8px",
  },
  riskTab: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.white,
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    fontWeight: 600,
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "solid", BRAND.border),
    transitionProperty: "border-color, background-color, color",
    transitionDuration: "120ms",
  },
  riskTabActive: {
    backgroundColor: BRAND.pillarOrange,
    color: BRAND.white,
    ...shorthands.borderColor(BRAND.pillarOrange),
  },
  blocker: {
    backgroundColor: "#fff5e6",
    color: "#7a4a10",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
    fontSize: "0.88rem",
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "solid", "#f3d4a3"),
  },
});

type Answers = {
  contact: ContactSummary | null;
  vorvertrag: VorvertragInfo;
  versicherer: VersichererKey;
  vertragsart: VertragsartKey | "";
  tarifierung: TarifierungKey | "";
  riskObjectIds: number[];
  /** Resolved klasse per risk id (falls automatic detection fails). */
  klasseByRisk: Record<number, KlasseKey | null>;
  /** Editable Wert/Summen fields per risk — defaults to the risk's characteristics. */
  werteByRisk: Record<number, Record<string, string>>;
  /** Contract-level Gefahren (Einzel-Tarife: standardvertrag, gewerbeKompakt). */
  gefahren: GefahrenAnswers;
  /** Per-risk Gefahren (Rahmen-Tarife: rahmenSammel, rahmenStueck). riskId → answers. */
  gefahrenPerRisk: Record<number, GefahrenAnswers>;
  /** Sammelvertrag premium grid (Klassen × Deckungen × Brutto). */
  sammelGrid: SammelGridState;
  /** Sammelvertrag: pro Risiko gewählte Deckungsspalte (SAMMELGRID_COLUMNS.key). */
  umfangAssignment: Record<number, string>;
  /** Demo-Toggle in der Vereinbarungs-Sektion: drei Phasen der Vereinbarung
   *  zwischen Courté und dem Versicherer. Steuert die UI im Vereinbarungs-Step
   *  und das Grey-Out der nachgelagerten Tabs. */
  vereinbarungStatus: "keine" | "moeglich" | "vereinbart";
  /** Zahlung / Bankverbindung answers (separater Step nach dem Versicherungsumfang). */
  zahlung: GefahrenAnswers;
  /** Legacy: Vertragsmaske-Daten — read-only nach Übermittlung; nicht mehr im Eingabe-Flow. */
  vertrags: VertragsAnswers;
};

const initialAnswers = (): Answers => ({
  contact: null,
  vorvertrag: { exists: "" },
  versicherer: "",
  vertragsart: "",
  tarifierung: "",
  riskObjectIds: [],
  klasseByRisk: {},
  werteByRisk: {},
  gefahren: {},
  gefahrenPerRisk: {},
  sammelGrid: initialSammelGrid(),
  umfangAssignment: {},
  vereinbarungStatus: "vereinbart",
  zahlung: {},
  vertrags: {},
});

// Per-risk Versicherungsumfang gilt für Rahmen-Tarife (eine Vertragsnummer oder
// Bündel mit mehreren Risiken — jedes Risiko bekommt individuelle Antworten).
const isPerRiskTarif = (t: TarifierungKey | ""): boolean =>
  t === "rahmenSammel" || t === "rahmenStueck";

// Resolve klasse from risk characteristics, if available.
const resolveKlasseFromRisk = (chars: Record<string, string>): KlasseKey | null => {
  const v = chars.vehicleClass ?? "";
  if (!v) return null;
  for (const m of VEHICLE_CLASS_TO_KEY) {
    if (v.includes(m.contains)) return m.key;
  }
  return null;
};

const buildDisplay = (c: any, fallbackId: number): string => {
  if (!c) return `Kontakt ${fallbackId}`;
  if (typeof c.displayName === "string" && c.displayName.trim()) return c.displayName;
  const first = c.firstName ?? "";
  const last = c.lastname ?? "";
  if (last && first) return `${last}, ${first}`;
  if (last) return String(last);
  if (first) return String(first);
  if (c.companyName) return String(c.companyName);
  return `Kontakt ${fallbackId}`;
};

const buildContactSummary = (c: any, fallbackId: number, role?: string): ContactSummary => ({
  id: c?.id ?? fallbackId,
  displayName: buildDisplay(c, fallbackId),
  role,
  street: c?.defaultAddress?.street ?? c?.address?.street,
  postalCode: c?.defaultAddress?.postalCode ?? c?.address?.postalCode,
  city: c?.defaultAddress?.city ?? c?.address?.city,
  country: c?.defaultAddress?.country ?? c?.address?.country,
});

const fetchRiskById = async (id: number): Promise<RiskSummary | null> => {
  try {
    const res = await fetch(apiUrl(`risk-objects/${id}`));
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const charsArr: Array<{ nameBezeichnung?: string; riskCharacteristicValue?: string }> =
      Array.isArray(data.characteristics) ? data.characteristics : [];
    const characteristics: Record<string, string> = {};
    for (const c of charsArr) {
      if (c.nameBezeichnung) {
        characteristics[c.nameBezeichnung] = String(c.riskCharacteristicValue ?? "");
      }
    }

    const contactList: ContactSummary[] = [];
    const directContact = data.contact;
    if (directContact && directContact.id) {
      contactList.push(buildContactSummary(directContact, directContact.id, "Halter"));
    }
    const additional = Array.isArray(data.contacts) ? data.contacts : [];
    for (const c of additional) {
      if (!c) continue;
      const id2 = c.id ?? c.contactId;
      if (!id2 || contactList.some((x) => x.id === id2)) continue;
      contactList.push(buildContactSummary(c, id2, c.role ?? "Versicherungsnehmer"));
    }
    return {
      id: data.id,
      name: data.name ?? "",
      characteristics,
      contacts: contactList,
    };
  } catch (e) {
    console.error("fetchRiskById failed", id, e);
    return null;
  }
};

export type KfzContractCreateEmbedded = {
  contactId?: number | null;
  riskObjectId?: number | null;
  riskObjectIds?: number[];
  contractId?: number | null;
  onSuccess?: (createdId: number | null) => void;
};

type KfzContractCreatePageProps = {
  /**
   * Wenn gesetzt, wird die Page als eingebettete Form innerhalb eines
   * Window-Manager-Fensters gerendert. URL/Router-Reads (useParams,
   * useSearchParams) werden durch die Props ersetzt; nach erfolgreichem
   * Speichern wird onSuccess gerufen statt zu navigieren.
   */
  embedded?: KfzContractCreateEmbedded;
};

const KfzContractCreatePage = ({ embedded }: KfzContractCreatePageProps = {}) => {
  const styles = useStyles();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const isEmbedded = Boolean(embedded);
  const routeRiskObjectId = isEmbedded ? undefined : params.riskObjectId;
  const editContractId = isEmbedded
    ? (embedded?.contractId != null ? String(embedded.contractId) : null)
    : searchParams.get("contractId") ?? null;
  const toasterId = "kfz-contract-toaster";
  const { dispatchToast } = useToastController(toasterId);

  // Preselected risk IDs: either single via /risks/:riskObjectId/contracts/new-kfz
  // or multiple via ?risks=1,2,3 query param (from Risks table / Contact details).
  // Im embedded-Mode kommen die IDs als Props.
  const embeddedRisksKey = isEmbedded
    ? (embedded?.riskObjectIds ?? []).join(",") + "|" + (embedded?.riskObjectId ?? "")
    : "";
  const preselectedRiskIds = useMemo<number[]>(() => {
    const ids = new Set<number>();
    if (isEmbedded) {
      if (embedded?.riskObjectId) {
        const n = Number(embedded.riskObjectId);
        if (Number.isFinite(n) && n > 0) ids.add(n);
      }
      for (const r of embedded?.riskObjectIds ?? []) {
        const n = Number(r);
        if (Number.isFinite(n) && n > 0) ids.add(n);
      }
      return Array.from(ids);
    }
    if (routeRiskObjectId) {
      const n = Number(routeRiskObjectId);
      if (Number.isFinite(n) && n > 0) ids.add(n);
    }
    const queryRisks = searchParams.get("risks");
    if (queryRisks) {
      for (const piece of queryRisks.split(",")) {
        const n = Number(piece.trim());
        if (Number.isFinite(n) && n > 0) ids.add(n);
      }
    }
    return Array.from(ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmbedded, embeddedRisksKey, routeRiskObjectId, searchParams.get("risks")]);

  // Optional ?contactId=… for entries coming from Contact details with no risks preselected.
  const preselectedContactId = useMemo<number | null>(() => {
    if (isEmbedded) {
      const n = Number(embedded?.contactId);
      return Number.isFinite(n) && n > 0 ? n : null;
    }
    const c = searchParams.get("contactId");
    if (!c) return null;
    const n = Number(c);
    return Number.isFinite(n) && n > 0 ? n : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmbedded, embedded?.contactId, searchParams.get("contactId")]);

  const [risks, setRisks] = useState<RiskSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Answers>(initialAnswers());
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null);
  const [currentSectionIdx, setCurrentSectionIdx] = useState(0);
  const [currentSubStepIdx, setCurrentSubStepIdx] = useState(0);
  const [activeRiskIdForInfo, setActiveRiskIdForInfo] = useState<number | null>(null);

  const draftKey = useMemo(() => {
    const key = preselectedRiskIds.length
      ? preselectedRiskIds.slice().sort((a, b) => a - b).join("-")
      : "new";
    return `${DRAFT_LS_PREFIX}${key}`;
  }, [preselectedRiskIds]);

  // Initial load: if we have preselected risks from URL, load them all and preselect them.
  // Also auto-fill the contact from the first risk's Halter, or from ?contactId=.
  useEffect(() => {
    const run = async () => {
      // Load contact via ?contactId=… when we don't have any preselected risks.
      if (preselectedRiskIds.length === 0 && preselectedContactId) {
        try {
          const res = await fetch(apiUrl(`contact/${preselectedContactId}`));
          if (res.ok) {
            const c = await res.json();
            const summary = buildContactSummary(c, preselectedContactId, "Halter");
            setAnswers((prev) => (prev.contact ? prev : { ...prev, contact: summary }));
          }
        } catch (e) {
          console.error("Failed to load contact", preselectedContactId, e);
        }
        setLoading(false);
        return;
      }
      if (preselectedRiskIds.length === 0) {
        setLoading(false);
        return;
      }
      const loaded: RiskSummary[] = [];
      for (const id of preselectedRiskIds) {
        const r = await fetchRiskById(id);
        if (r) loaded.push(r);
      }
      if (loaded.length === 0) {
        setError("Risikoobjekt(e) konnte(n) nicht geladen werden.");
        setLoading(false);
        return;
      }
      setRisks(loaded);
      setAnswers((prev) => {
        const next = { ...prev, klasseByRisk: { ...prev.klasseByRisk } };
        const ids = new Set(prev.riskObjectIds);
        for (const r of loaded) {
          if (!ids.has(r.id)) {
            prev.riskObjectIds.push(r.id);
            ids.add(r.id);
          }
          if (next.klasseByRisk[r.id] === undefined) {
            next.klasseByRisk[r.id] = resolveKlasseFromRisk(r.characteristics);
          }
        }
        // Auto-fill contact from the first loaded risk's Halter, if not yet set.
        const contact = prev.contact ?? loaded[0]?.contacts[0] ?? null;
        return { ...next, contact, riskObjectIds: Array.from(ids) };
      });
      setLoading(false);
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedRiskIds.join(","), preselectedContactId]);

  // Sync selected risks ↔ loaded risk summaries.
  // IMPORTANT: this effect must NOT depend on `risks` and must NOT return a new
  // array reference when nothing actually changed — otherwise it loops forever
  // and freezes the main thread (blocking route navigation).
  useEffect(() => {
    const idSet = new Set(answers.riskObjectIds);
    // Prune any loaded summaries that are no longer in the selection.
    setRisks((prev) => {
      const next = prev.filter((r) => idSet.has(r.id));
      return next.length === prev.length ? prev : next;
    });

    let cancelled = false;
    (async () => {
      // Snapshot what's already loaded via a setState callback so we don't
      // depend on `risks` in the deps array.
      let currentlyLoaded: RiskSummary[] = [];
      setRisks((prev) => {
        currentlyLoaded = prev;
        return prev;
      });
      const missing = answers.riskObjectIds.filter(
        (id) => !currentlyLoaded.some((r) => r.id === id),
      );
      if (missing.length === 0) return;

      const loaded: RiskSummary[] = [];
      for (const id of missing) {
        const r = await fetchRiskById(id);
        if (r) loaded.push(r);
      }
      if (cancelled || loaded.length === 0) return;
      setRisks((prev) => {
        const existing = new Set(prev.map((r) => r.id));
        const toAdd = loaded.filter((r) => !existing.has(r.id));
        return toAdd.length === 0 ? prev : [...prev, ...toAdd];
      });
      setAnswers((prev) => {
        const next = { ...prev, klasseByRisk: { ...prev.klasseByRisk } };
        let changed = false;
        for (const r of loaded) {
          if (next.klasseByRisk[r.id] === undefined) {
            next.klasseByRisk[r.id] = resolveKlasseFromRisk(r.characteristics);
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [answers.riskObjectIds]);

  // Track active risk for Risikoinfo tab — default to first selected.
  useEffect(() => {
    if (!answers.riskObjectIds.length) {
      setActiveRiskIdForInfo(null);
      return;
    }
    if (!activeRiskIdForInfo || !answers.riskObjectIds.includes(activeRiskIdForInfo)) {
      setActiveRiskIdForInfo(answers.riskObjectIds[0]);
    }
  }, [answers.riskObjectIds, activeRiskIdForInfo]);

  // ── Edit-Mode: fetch contract und prefill ───────────────────────────
  // Wenn editContractId gesetzt ist, laden wir den existierenden Vertrag
  // und befüllen `answers` aus den characteristics-Strings + JSON-Blobs,
  // die buildPayload() ursprünglich geschrieben hat.
  const editPrefillDoneRef = useRef(false);
  useEffect(() => {
    if (!editContractId) return;
    if (editPrefillDoneRef.current) return;
    editPrefillDoneRef.current = true;
    let aborted = false;
    (async () => {
      try {
        const res = await fetch(apiUrl(`contracts/${editContractId}`));
        if (!res.ok) return;
        const contract = await res.json();
        if (aborted) return;
        const charsArr: Array<{
          nameBezeichnung?: string;
          contractCharacteristicValue?: string;
        }> = Array.isArray(contract.characteristics) ? contract.characteristics : [];
        const charByName = new Map<string, string>();
        for (const c of charsArr) {
          if (c.nameBezeichnung) charByName.set(c.nameBezeichnung, c.contractCharacteristicValue ?? "");
        }
        const read = (k: string): string => charByName.get(k) ?? "";
        const parseJson = <T,>(k: string, fallback: T): T => {
          const raw = read(k);
          if (!raw) return fallback;
          try {
            return JSON.parse(raw) as T;
          } catch {
            return fallback;
          }
        };
        const gefahrenBlob = parseJson<{ mode?: string; data?: unknown }>(
          "kfz.gefahren_json",
          {},
        );
        const sammelBlob = parseJson<{
          klassen?: SammelGridState["klassen"];
          agreementFile?: SammelGridState["agreementFile"];
        }>("kfz.sammelgrid_json", {});
        const werteByRiskBlob = parseJson<Record<number, Record<string, string>>>(
          "kfz.werte_json",
          {},
        );
        const zahlungBlob = parseJson<GefahrenAnswers>("kfz.zahlung_json", {});
        const umfangBlob = parseJson<Record<number, string>>(
          "kfz.umfang_assignment_json",
          {},
        );
        const riskIdsBlob = parseJson<number[]>("kfz.risk_object_ids", []);
        const riskIdsFromContract = Array.isArray(contract.riskObjects)
          ? contract.riskObjects
              .map((r: { id?: number }) => r?.id)
              .filter((id: number | undefined): id is number => Number.isFinite(id))
          : [];
        const riskIds = riskIdsBlob.length > 0 ? riskIdsBlob : riskIdsFromContract;

        setAnswers((prev) => {
          const next: Answers = {
            ...prev,
            versicherer: (read("kfz.versicherer") || prev.versicherer) as Answers["versicherer"],
            vertragsart: (read("kfz.vertragsart") || prev.vertragsart) as Answers["vertragsart"],
            tarifierung: (read("kfz.tarifierungsart") || prev.tarifierung) as Answers["tarifierung"],
            vorvertrag: {
              exists: (read("kfz.vorvertrag_exists") || prev.vorvertrag.exists) as VorvertragInfo["exists"],
              isFremdvertrag: (read("kfz.vorvertrag_is_fremdvertrag") || undefined) as VorvertragInfo["isFremdvertrag"],
              existingContractId: read("kfz.vorvertrag_existing_id") || undefined,
              gesellschaft: read("kfz.vorvertrag_gesellschaft") || undefined,
              vertragsnummer: read("kfz.vorvertrag_nummer") || undefined,
              alreadyCancelled: (read("kfz.vorvertrag_already_cancelled") || undefined) as VorvertragInfo["alreadyCancelled"],
              kuendigungZum: read("kfz.vorvertrag_kuendigung_zum") || undefined,
              kuendigungsgrund: read("kfz.vorvertrag_kuendigungsgrund") || undefined,
              shouldCancel: (read("kfz.vorvertrag_should_cancel") || undefined) as VorvertragInfo["shouldCancel"],
            },
            riskObjectIds: riskIds.length > 0 ? riskIds : prev.riskObjectIds,
            werteByRisk: Object.keys(werteByRiskBlob).length > 0 ? werteByRiskBlob : prev.werteByRisk,
            umfangAssignment:
              Object.keys(umfangBlob).length > 0 ? umfangBlob : prev.umfangAssignment,
            zahlung: Object.keys(zahlungBlob).length > 0 ? zahlungBlob : prev.zahlung,
            vereinbarungStatus:
              (read("kfz.vereinbarung_status") as Answers["vereinbarungStatus"]) ||
              prev.vereinbarungStatus,
          };
          // Gefahren je nach mode in das richtige Bag.
          if (gefahrenBlob.mode === "per-risk" && gefahrenBlob.data) {
            next.gefahrenPerRisk = gefahrenBlob.data as Record<number, GefahrenAnswers>;
          } else if (gefahrenBlob.data) {
            next.gefahren = gefahrenBlob.data as GefahrenAnswers;
          }
          // Sammelgrid (Klassen-Definition) — agreementFile-DataURL geht beim
          // Edit verloren (wurde beim ersten Save eh nicht persistiert).
          if (sammelBlob.klassen) {
            next.sammelGrid = {
              ...prev.sammelGrid,
              klassen: sammelBlob.klassen,
              agreementFile: sammelBlob.agreementFile ?? prev.sammelGrid.agreementFile,
            };
          }
          return next;
        });

        // Kontakt nachladen (für Header-Anzeige + Save-Payload).
        if (contract.contactId) {
          try {
            const cRes = await fetch(apiUrl(`contact/${contract.contactId}`));
            if (cRes.ok) {
              const c = await cRes.json();
              if (!aborted) {
                const summary = buildContactSummary(c, contract.contactId, "Versicherungsnehmer");
                setAnswers((prev) => ({ ...prev, contact: summary }));
              }
            }
          } catch {
            /* ignore */
          }
        }
      } catch (err) {
        console.error("Edit-Mode contract fetch failed", err);
      }
    })();
    return () => {
      aborted = true;
    };
  }, [editContractId]);

  // Restore draft
  const draftLoadedRef = useRef(false);
  useEffect(() => {
    if (loading || draftLoadedRef.current) return;
    draftLoadedRef.current = true;
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          setAnswers((prev) => ({ ...prev, ...parsed.answers }));
          const totalSecs = KFZ_CONTRACT_SECTIONS.length;
          const restoredSec = Math.min(
            Math.max(0, parsed.currentSectionIdx ?? 0),
            totalSecs - 1,
          );
          const maxStep = (KFZ_CONTRACT_SECTIONS[restoredSec]?.steps.length ?? 1) - 1;
          const restoredStep = Math.min(
            Math.max(0, parsed.currentSubStepIdx ?? 0),
            maxStep,
          );
          setCurrentSectionIdx(restoredSec);
          setCurrentSubStepIdx(restoredStep);
          setDraftSavedAt(parsed.savedAt ?? null);
        }
      }
    } catch {
      // ignore
    }
    // Wenn die Detail-View per `?section=…` zur Bearbeitung herkommt, springen
    // wir auf diese Sektion (überschreibt evtl. wiederhergestellten Stand).
    const sectionParam = searchParams.get("section");
    if (sectionParam) {
      const idx = KFZ_CONTRACT_SECTIONS.findIndex((s) => s.key === sectionParam);
      if (idx >= 0) {
        setCurrentSectionIdx(idx);
        setCurrentSubStepIdx(0);
      }
    }
  }, [loading, draftKey]);

  // Autosave
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (loading) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => {
      const savedAt = new Date().toISOString();
      try {
        localStorage.setItem(
          draftKey,
          JSON.stringify({ answers, currentSectionIdx, currentSubStepIdx, savedAt }),
        );
        setDraftSavedAt(savedAt);
      } catch {
        // ignore
      }
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, [answers, currentSectionIdx, currentSubStepIdx, draftKey, loading]);

  // Vereinbarung-Mock für die aktuelle (Versicherer × Tarifierung)-Kombination.
  const vereinbarungMockRaw = useMemo(
    () => getVereinbarungMock(answers.versicherer, answers.tarifierung),
    [answers.versicherer, answers.tarifierung],
  );
  // Mock nur als „echte" Vereinbarung verwenden, wenn der Demo-Status auf
  // „vereinbart" steht.
  const vereinbarungMock =
    answers.vereinbarungStatus === "vereinbart" ? vereinbarungMockRaw : null;
  // Vereinbarungs-Tab ist sichtbar, sobald ein Rahmen-Tarif (Sammelprämie oder
  // Stückprämie) + Versicherer gewählt sind. Solange wir nicht im Status
  // „vereinbart" sind, werden die nachgelagerten Sektionen ausgegraut.
  const showVereinbarung =
    !!answers.versicherer && isPerRiskTarif(answers.tarifierung);
  const blockDownstream =
    showVereinbarung && answers.vereinbarungStatus !== "vereinbart";

  const sections = useMemo(
    () =>
      KFZ_CONTRACT_SECTIONS.filter((s) => s.key !== "vereinbarung" || showVereinbarung),
    [showVereinbarung],
  );
  const vereinbarungIdx = useMemo(
    () => sections.findIndex((s) => s.key === "vereinbarung"),
    [sections],
  );
  const totalSteps = useMemo(
    () => sections.reduce((acc, s) => acc + s.steps.length, 0),
    [sections],
  );
  const stepIndex = useMemo(() => {
    let idx = 0;
    for (let s = 0; s < currentSectionIdx; s++) idx += sections[s].steps.length;
    return idx + currentSubStepIdx;
  }, [sections, currentSectionIdx, currentSubStepIdx]);
  const progressFraction = totalSteps > 0 ? stepIndex / Math.max(1, totalSteps - 1) : 0;
  // Clamp the active indices whenever the visible section list shrinks.
  useEffect(() => {
    if (sections.length === 0) return;
    if (currentSectionIdx >= sections.length) {
      setCurrentSectionIdx(sections.length - 1);
      setCurrentSubStepIdx(0);
      return;
    }
    const maxStep = (sections[currentSectionIdx]?.steps.length ?? 1) - 1;
    if (currentSubStepIdx > maxStep) {
      setCurrentSubStepIdx(Math.max(0, maxStep));
    }
  }, [sections, currentSectionIdx, currentSubStepIdx]);
  // Submit button erscheint am Ende der letzten Sektion ("Vertragsdaten und Zahlung").
  const submitSectionIdx = sections.length - 1;
  const isSubmitStep =
    currentSectionIdx === submitSectionIdx &&
    currentSubStepIdx === (sections[submitSectionIdx]?.steps.length ?? 1) - 1;

  const goPrev = () => {
    if (currentSubStepIdx > 0) {
      setCurrentSubStepIdx(currentSubStepIdx - 1);
    } else if (currentSectionIdx > 0) {
      const prev = currentSectionIdx - 1;
      setCurrentSectionIdx(prev);
      setCurrentSubStepIdx(sections[prev].steps.length - 1);
    }
  };
  const goNext = () => {
    const sec = sections[currentSectionIdx];
    if (currentSubStepIdx < sec.steps.length - 1) {
      setCurrentSubStepIdx(currentSubStepIdx + 1);
      return;
    }
    const nextIdx = currentSectionIdx + 1;
    if (nextIdx >= sections.length) return;
    // Block navigation past the Vereinbarung section until a Vereinbarung exists.
    if (blockDownstream && currentSectionIdx >= vereinbarungIdx) return;
    setCurrentSectionIdx(nextIdx);
    setCurrentSubStepIdx(0);
  };

  const saveDraftNow = () => {
    setSavingDraft(true);
    const savedAt = new Date().toISOString();
    try {
      localStorage.setItem(
        draftKey,
        JSON.stringify({ answers, currentSectionIdx, currentSubStepIdx, savedAt }),
      );
      setDraftSavedAt(savedAt);
    } catch {
      // ignore
    } finally {
      setTimeout(() => setSavingDraft(false), 250);
    }
  };

  const onChangeGefahren = (key: string, value: string) => {
    setAnswers((prev) => ({ ...prev, gefahren: { ...prev.gefahren, [key]: value } }));
  };
  const onChangeZahlung = (key: string, value: string) => {
    setAnswers((prev) => ({ ...prev, zahlung: { ...prev.zahlung, [key]: value } }));
  };
  // The contract-level klasse used to drive Gefahren / Risikoinfo defaults.
  // Use the first selected risk's klasse.
  const primaryKlasse: KlasseKey | null = useMemo(() => {
    const firstId = answers.riskObjectIds[0];
    if (!firstId) return null;
    const fromMap = answers.klasseByRisk[firstId];
    if (fromMap !== undefined && fromMap !== null) return fromMap;
    const firstRisk = risks.find((r) => r.id === firstId);
    return firstRisk ? resolveKlasseFromRisk(firstRisk.characteristics) : null;
  }, [answers.riskObjectIds, answers.klasseByRisk, risks]);

  const primaryRisk: RiskSummary | null =
    risks.find((r) => r.id === answers.riskObjectIds[0]) ?? null;

  const buildPayload = () => {
    if (answers.riskObjectIds.length === 0) return null;
    const a = answers;
    const characteristics: Array<{ nameBezeichnung: string; contractCharacteristicValue: string }> = [];
    const push = (name: string, value: string | number | null | undefined) => {
      if (value === null || value === undefined || value === "") return;
      characteristics.push({ nameBezeichnung: name, contractCharacteristicValue: String(value) });
    };

    push("kfz.vorvertrag_exists", a.vorvertrag.exists);
    push("kfz.vorvertrag_is_fremdvertrag", a.vorvertrag.isFremdvertrag);
    push("kfz.vorvertrag_existing_id", a.vorvertrag.existingContractId);
    push("kfz.vorvertrag_gesellschaft", a.vorvertrag.gesellschaft);
    push("kfz.vorvertrag_nummer", a.vorvertrag.vertragsnummer);
    push("kfz.vorvertrag_already_cancelled", a.vorvertrag.alreadyCancelled);
    push("kfz.vorvertrag_kuendigung_zum", a.vorvertrag.kuendigungZum);
    push("kfz.vorvertrag_kuendigungsgrund", a.vorvertrag.kuendigungsgrund);
    push("kfz.vorvertrag_should_cancel", a.vorvertrag.shouldCancel);
    push("kfz.versicherer", a.versicherer);
    push("kfz.vertragsart", a.vertragsart);
    push("kfz.tarifierungsart", a.tarifierung);
    push("kfz.klasse", primaryKlasse ?? "");
    if (a.tarifierung === "rahmenSammel") {
      // Klassen-Definition (pro Klasse parent + subs) + Agreement-Datei-Metadata.
      // File-Bytes (dataUrl) werden nicht im Payload mitgesendet — nur Name/Größe.
      const sammelForPayload = {
        klassen: a.sammelGrid.klassen,
        agreementFile: a.sammelGrid.agreementFile
          ? {
              name: a.sammelGrid.agreementFile.name,
              size: a.sammelGrid.agreementFile.size,
            }
          : undefined,
      };
      push("kfz.sammelgrid_json", JSON.stringify(sammelForPayload));
      push("kfz.deckungen_count", String(countSelections(a.sammelGrid)));
      push("kfz.vereinbarung_status", a.vereinbarungStatus);
      if (Object.keys(a.umfangAssignment).length) {
        push("kfz.umfang_assignment_json", JSON.stringify(a.umfangAssignment));
      }
    }
    const perRiskMode = isPerRiskTarif(a.tarifierung);
    push(
      "kfz.gefahren_json",
      JSON.stringify(
        perRiskMode
          ? { mode: "per-risk", data: a.gefahrenPerRisk }
          : { mode: "contract", data: a.gefahren },
      ),
    );
    if (Object.keys(a.werteByRisk).length) {
      push("kfz.werte_json", JSON.stringify(a.werteByRisk));
    }
    push("kfz.zahlung_json", JSON.stringify(a.zahlung));
    push("kfz.risk_object_ids", JSON.stringify(a.riskObjectIds));

    const ownerId = answers.contact?.id ?? primaryRisk?.contacts[0]?.id ?? null;
    const contractNbr = `KFZ-${Date.now()}`;
    return {
      contractNbr,
      branch: "KFZ",
      contactId: ownerId,
      currency: "EUR",
      // „Collective" sobald mehr als ein Risiko angehängt ist — das matched
      // die Backend-Validierung (Einzelvertrag = genau 1 Risiko) sauberer als
      // die reine Vertragsart-Ableitung.
      isCollectiveContract:
        a.riskObjectIds.length > 1 ||
        a.vertragsart === "buendel" ||
        a.vertragsart === "sammel",
      riskObjectIds: a.riskObjectIds,
      characteristics,
    };
  };

  const onSubmit = async () => {
    const payload = buildPayload();
    if (!payload) {
      dispatchToast(
        <Toast>
          <ToastTitle>Bitte mindestens ein Risiko auswählen</ToastTitle>
        </Toast>,
        { intent: "warning" },
      );
      return;
    }
    setSubmitting(true);
    try {
      const isEdit = Boolean(editContractId);
      const url = isEdit ? apiUrl(`contracts/${editContractId}`) : apiUrl("contracts");
      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const text = await res.text();
        console.error("Contract POST failed", res.status, text);
        let detail = "";
        try {
          const parsed = JSON.parse(text);
          const errors = parsed?.message?.errors ?? parsed?.errors;
          if (errors && typeof errors === "object") {
            detail = Object.values(errors).filter(Boolean).join(" · ");
          }
          if (!detail && typeof parsed?.message === "string") {
            detail = parsed.message;
          }
        } catch {
          if (text) detail = text.slice(0, 200);
        }
        dispatchToast(
          <Toast>
            <ToastTitle>
              Speichern fehlgeschlagen{detail ? ` — ${detail}` : ""}
            </ToastTitle>
          </Toast>,
          { intent: "error" },
        );
        return;
      }
      let createdId: number | null = isEdit ? Number(editContractId) : null;
      try {
        const created = await res.json();
        createdId = created?.id ?? createdId;
      } catch {
        // empty body — still treat as success
      }
      try {
        localStorage.removeItem(draftKey);
      } catch {
        // ignore
      }
      dispatchToast(
        <Toast>
          <ToastTitle>{isEdit ? "Vertrag aktualisiert" : "Vertrag erstellt"}</ToastTitle>
        </Toast>,
        { intent: "success" },
      );
      if (isEmbedded) {
        embedded?.onSuccess?.(createdId);
      } else if (createdId) {
        navigate(`/contracts/${createdId}`);
      } else {
        navigate("/contracts");
      }
    } catch (e) {
      console.error("Contract POST threw", e);
      dispatchToast(
        <Toast>
          <ToastTitle>Speichern fehlgeschlagen</ToastTitle>
        </Toast>,
        { intent: "error" },
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>
            <span className={styles.titleChipIcon} aria-hidden="true">
              <DocumentAdd20Regular />
            </span>
            Vertrag anlegen
            <span className={styles.titleChip}>
              <span className={styles.titleChipIcon} aria-hidden="true">
                <VehicleCarProfile20Regular />
              </span>
              KFZ-Versicherung
            </span>
          </h1>
          <Button appearance="subtle" onClick={() => navigate(-1)}>
            Zurück
          </Button>
        </div>
        <div className={styles.formPanel}>
          <div className={styles.loadingState}>Lade Risikoobjekt…</div>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>
            <span className={styles.titleChipIcon} aria-hidden="true">
              <DocumentAdd20Regular />
            </span>
            Vertrag anlegen
            <span className={styles.titleChip}>
              <span className={styles.titleChipIcon} aria-hidden="true">
                <VehicleCarProfile20Regular />
              </span>
              KFZ-Versicherung
            </span>
          </h1>
          <Button appearance="subtle" onClick={() => navigate(-1)}>
            Zurück
          </Button>
        </div>
        <div className={styles.formPanel}>
          <div className={styles.errorState}>{error}</div>
        </div>
      </div>
    );
  }

  const section = sections[currentSectionIdx] ?? sections[0];
  const step = section.steps[currentSubStepIdx] ?? section.steps[0];
  const accent = sectionColorHex[section.color];

  const tarifLabel =
    TARIFIERUNGSARTEN.find((t) => t.key === answers.tarifierung)?.label ?? "—";
  const vertragsartLabel =
    VERTRAGSARTEN.find((v) => v.key === answers.vertragsart)?.label ?? "—";
  const klasseLabel = primaryKlasse ? KLASSEN_LABELS[primaryKlasse] : "—";

  const stepBlockerMessage = (kind: ContractStepKind): string | null => {
    if (
      (kind === "vertragsBasis" ||
        kind === "risikoInfo" ||
        kind === "gefahren" ||
        kind === "vereinbarung" ||
        kind === "zahlung" ||
        kind === "vertragsInfo" ||
        kind === "summary") &&
      !answers.contact
    ) {
      return "Bitte zuerst den Versicherungsnehmer auswählen.";
    }
    if (
      (kind === "gefahren" ||
        kind === "zahlung" ||
        kind === "vertragsInfo" ||
        kind === "summary") &&
      answers.riskObjectIds.length === 0
    ) {
      return "Bitte mindestens ein Risiko auswählen, bevor Sie fortfahren.";
    }
    if (
      (kind === "gefahren" || kind === "zahlung") &&
      !answers.tarifierung &&
      answers.vertragsart !== "buendel"
    ) {
      return "Bitte zuerst die Tarifierungsart auswählen — der Versicherungsumfang hängt davon ab.";
    }
    return null;
  };

  const renderStep = (kind: ContractStepKind) => {
    switch (kind) {
      case "kontaktRisiko":
        return (
          <div style={{ display: "flex", flexDirection: "column", rowGap: 28 }}>
            <div style={{ display: "flex", flexDirection: "column", rowGap: 16 }}>
              <ContactSelector
                label="Versicherungsnehmer / Halter"
                placeholder="Kontakt suchen (Name, Firma)…"
                selectedContact={answers.contact}
                onSelect={(c) => {
                  if (!c) return;
                  const summary = buildContactSummary(c, c.id ?? 0, "Halter");
                  setAnswers((p) => {
                    const sameContact = p.contact?.id === summary.id;
                    const next: Answers = sameContact
                      ? { ...p, contact: summary }
                      : { ...p, contact: summary, riskObjectIds: [], klasseByRisk: {} };
                    return next;
                  });
                }}
              />
              {answers.contact && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    rowGap: 4,
                    padding: "12px 14px",
                    backgroundColor: BRAND.bg,
                    borderRadius: 8,
                    border: `1px solid ${BRAND.borderDashed}`,
                  }}
                >
                  <span style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: BRAND.muted }}>
                    Ausgewählter Halter
                  </span>
                  <span style={{ fontFamily: BRAND.fontAccent, fontSize: "1.15rem", color: BRAND.ink }}>
                    {answers.contact.displayName}
                  </span>
                  <span style={{ fontSize: "0.85rem", color: BRAND.muted }}>
                    {[answers.contact.street, answers.contact.postalCode, answers.contact.city]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </div>
              )}
            </div>
            <RisikoAuswahlSection
              preselectedRiskIds={preselectedRiskIds}
              contactIdHint={answers.contact?.id ?? primaryRisk?.contacts[0]?.id ?? null}
              selectedIds={answers.riskObjectIds}
              onChange={(ids) =>
                setAnswers((p) => ({
                  ...p,
                  riskObjectIds: ids,
                }))
              }
              isEinzelvertrag={answers.vertragsart === "einzel"}
              autoSelectAll={answers.vertragsart === "sammel"}
              preloaded={risks}
            />
          </div>
        );
      case "vertragsBasis":
        return (
          <div style={{ display: "flex", flexDirection: "column", rowGap: 24 }}>
            <VertragsBasisSection
              vorvertrag={answers.vorvertrag}
              versicherer={answers.versicherer}
              vertragsart={answers.vertragsart}
              tarifierung={answers.tarifierung}
              onVorvertragChange={(v) => setAnswers((p) => ({ ...p, vorvertrag: v }))}
              onVersichererChange={(v) => setAnswers((p) => ({ ...p, versicherer: v }))}
              onVertragsartChange={(v) =>
                setAnswers((p) => {
                  const current = TARIFIERUNGSARTEN.find((x) => x.key === p.tarifierung);
                  const stillValid = current?.availableFor.includes(v) ?? false;
                  let nextTarif: TarifierungKey | "" = stillValid ? p.tarifierung : "";
                  if (!stillValid && v === "sammel") nextTarif = "rahmenSammel";
                  if (v === "buendel") nextTarif = "";
                  const nextRiskIds =
                    v === "einzel" && p.riskObjectIds.length > 1
                      ? [p.riskObjectIds[0]]
                      : p.riskObjectIds;
                  return {
                    ...p,
                    vertragsart: v,
                    tarifierung: nextTarif,
                    riskObjectIds: nextRiskIds,
                  };
                })
              }
              onTarifierungChange={(v) => setAnswers((p) => ({ ...p, tarifierung: v }))}
            />
            {!primaryKlasse && answers.riskObjectIds.length > 0 && primaryRisk && (
              <div className={styles.klasseRow}>
                <div className={styles.klasseHeader}>
                  <span style={{ fontFamily: BRAND.fontAccent, fontSize: "1.1rem", color: BRAND.ink }}>
                    Fahrzeugklasse bestätigen
                  </span>
                  <span className={styles.klasseHeaderLabel} style={{ color: BRAND.muted, fontWeight: 400, fontSize: "0.85rem" }}>
                    Konnte nicht aus dem Risiko ermittelt werden — bitte wählen.
                  </span>
                </div>
                <VehicleClassPicker
                  value=""
                  onChange={(v) => {
                    const match = VEHICLE_CLASSES.find((vc) => vc.value === v);
                    if (match) {
                      setAnswers((p) => ({
                        ...p,
                        klasseByRisk: {
                          ...p.klasseByRisk,
                          [primaryRisk.id]: match.iconKey as KlasseKey,
                        },
                      }));
                    }
                  }}
                />
              </div>
            )}
          </div>
        );
      case "risikoInfo": {
        if (answers.riskObjectIds.length === 0 || !primaryRisk) {
          return (
            <div className={styles.blocker}>
              Bitte wählen Sie zuerst Risiken im vorherigen Schritt aus.
            </div>
          );
        }
        const activeId = activeRiskIdForInfo ?? answers.riskObjectIds[0];
        const activeRisk = risks.find((r) => r.id === activeId) ?? primaryRisk;
        const activeKlasse =
          answers.klasseByRisk[activeRisk.id] ??
          resolveKlasseFromRisk(activeRisk.characteristics);
        return (
          <div>
            {answers.riskObjectIds.length > 1 && (
              <div className={styles.riskTabsRow}>
                {answers.riskObjectIds.map((id) => {
                  const r = risks.find((x) => x.id === id);
                  if (!r) return null;
                  const label = r.characteristics.licensePlate || `Risiko ${id}`;
                  const active = activeId === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setActiveRiskIdForInfo(id)}
                      className={mergeClasses(styles.riskTab, active && styles.riskTabActive)}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            )}
            <RisikoinfoSection klasse={activeKlasse} risk={activeRisk} />
          </div>
        );
      }
      case "gefahren":
        if (answers.vertragsart === "buendel") {
          return (
            <div className={styles.blocker}>
              Bündelvertrag: der Versicherungsumfang wird je gebündeltem Einzelvertrag erfasst.
              Diese Sektion ist für Bündelverträge nicht aktiv.
            </div>
          );
        }
        if (isPerRiskTarif(answers.tarifierung)) {
          if (vereinbarungMock) {
            const selectedRisks = answers.riskObjectIds
              .map((id) => risks.find((r) => r.id === id))
              .filter((r): r is RiskSummary => !!r);
            return (
              <UmfangAssignmentTable
                klassenDefinition={answers.sammelGrid}
                vehicles={selectedRisks}
                klasseByRisk={answers.klasseByRisk}
                assignment={answers.umfangAssignment}
                onChange={(next) =>
                  setAnswers((p) => ({ ...p, umfangAssignment: next }))
                }
              />
            );
          }
          return (
            <div className={styles.blocker}>
              Versicherungsumfang ist erst zuweisbar, wenn eine Vereinbarung mit dem
              Versicherer abgeschlossen wurde. Bitte in der Sektion „Vereinbarung mit
              dem Versicherer" den Status auf „Vereinbart" setzen.
            </div>
          );
        }
        return (
          <GefahrenSection
            tarifierung={answers.tarifierung}
            klasse={primaryKlasse}
            answers={answers.gefahren}
            onChange={onChangeGefahren}
          />
        );
      case "vereinbarung": {
        const tarifLbl =
          TARIFIERUNGSARTEN.find((t) => t.key === answers.tarifierung)?.label ?? "—";
        const status = answers.vereinbarungStatus;
        const setStatus = (next: "keine" | "moeglich" | "vereinbart") =>
          setAnswers((p) => ({ ...p, vereinbarungStatus: next }));
        const STATES: Array<{
          key: "keine" | "moeglich" | "vereinbart";
          label: string;
        }> = [
          { key: "keine", label: "Keine Vereinbarung" },
          { key: "moeglich", label: "Mögliche Vereinbarung" },
          { key: "vereinbart", label: "Vereinbart" },
        ];
        const segmented = (
          <div
            role="tablist"
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 6,
              backgroundColor: BRAND.bg,
              padding: 4,
              borderRadius: 999,
              alignSelf: "flex-start",
              border: `1px solid ${BRAND.border}`,
            }}
          >
            {STATES.map((s) => {
              const active = s.key === status;
              return (
                <button
                  key={s.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setStatus(s.key)}
                  style={{
                    appearance: "none",
                    cursor: "pointer",
                    backgroundColor: active ? BRAND.purple : "transparent",
                    color: active ? BRAND.white : BRAND.ink,
                    border: `1px solid ${active ? BRAND.purple : "transparent"}`,
                    borderRadius: 999,
                    padding: "6px 14px",
                    fontFamily: BRAND.fontPrimary,
                    fontSize: "0.85rem",
                    fontWeight: 700,
                  }}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        );

        const klassendefBlock = (
          <div style={{ display: "flex", flexDirection: "column", rowGap: 12 }}>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                columnGap: 12,
                flexWrap: "wrap",
              }}
            >
              <h3
                style={{
                  fontFamily: BRAND.fontAccent,
                  fontSize: "1.2rem",
                  color: BRAND.ink,
                  margin: 0,
                }}
              >
                Klassen-Definition — {tarifLbl}
              </h3>
              <span style={{ fontSize: "0.85rem", color: BRAND.muted }}>
                {status === "keine" &&
                  "Pro Subklasse die gewünschten Deckungs-/SB-Varianten markieren. Die Auswahl wird dem Versicherer als Vorschlag übermittelt."}
                {status === "moeglich" &&
                  "Mögliche Brutto-Beträge in € für die markierten Varianten erfassen — als Grundlage für die Verhandlung mit dem Versicherer."}
                {status === "vereinbart" &&
                  "Vereinbarte Beiträge eintragen — Netto. Versicherungssteuer wird zentral oben gesetzt."}
              </span>
            </div>
            {status === "vereinbart" && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  columnGap: 12,
                  rowGap: 8,
                  backgroundColor: BRAND.bg,
                  border: `1px solid ${BRAND.border}`,
                  borderRadius: 10,
                  padding: "10px 14px",
                }}
              >
                <span
                  style={{
                    fontFamily: BRAND.fontPrimary,
                    fontSize: "0.78rem",
                    fontWeight: 700,
                    color: BRAND.muted,
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                  }}
                >
                  Versicherungssteuersatz
                </span>
                <input
                  type="text"
                  value={answers.sammelGrid.versicherungssteuersatz ?? ""}
                  placeholder="19,00"
                  onChange={(e) =>
                    setAnswers((p) => ({
                      ...p,
                      sammelGrid: {
                        ...p.sammelGrid,
                        versicherungssteuersatz: e.target.value,
                      },
                    }))
                  }
                  style={{
                    width: 90,
                    fontFamily: BRAND.fontPrimary,
                    fontSize: "0.9rem",
                    color: BRAND.ink,
                    backgroundColor: BRAND.white,
                    padding: "6px 10px",
                    borderRadius: 6,
                    border: `1px solid ${BRAND.border}`,
                    outline: "none",
                    textAlign: "right",
                    boxSizing: "border-box",
                  }}
                />
                <span style={{ fontSize: "0.85rem", color: BRAND.muted }}>%</span>
                <span
                  style={{
                    marginLeft: "auto",
                    fontSize: "0.82rem",
                    color: BRAND.muted,
                    fontStyle: "italic",
                  }}
                >
                  Hinweis: Beträge in der Tabelle bitte als <strong>Netto</strong> erfassen.
                </span>
              </div>
            )}
            <SammelvertragGrid
              grid={answers.sammelGrid}
              onChange={(next) => setAnswers((p) => ({ ...p, sammelGrid: next }))}
              mode={
                status === "keine"
                  ? "antrag"
                  : status === "moeglich"
                    ? "beitrag"
                    : "vereinbart"
              }
            />
          </div>
        );

        const fileBlock = status === "vereinbart" && (
          <VereinbarungFileUpload
            file={answers.sammelGrid.agreementFile}
            onChange={(f) =>
              setAnswers((p) => ({
                ...p,
                sammelGrid: { ...p.sammelGrid, agreementFile: f ?? undefined },
              }))
            }
          />
        );

        const keineHint = status === "keine" && (
          <div
            style={{
              backgroundColor: "#fff5e6",
              border: "1px solid #f3d4a3",
              borderRadius: 10,
              padding: "12px 16px",
              color: "#7a4a10",
              fontSize: "0.9rem",
              lineHeight: 1.5,
            }}
          >
            Für diese Produkt-/Tarifierungsart („{tarifLbl}") liegt noch keine Vereinbarung
            mit dem Versicherer vor. Definieren Sie unten die gewünschten Klassen-Subklassen
            mit ihren Deckungen — der Versicherer ermittelt die Selbstbeteiligungen.
          </div>
        );

        return (
          <div style={{ display: "flex", flexDirection: "column", rowGap: 14 }}>
            {segmented}
            {keineHint}
            {klassendefBlock}
            {fileBlock}
          </div>
        );
      }
      case "zahlung": {
        const isRahmen = isPerRiskTarif(answers.tarifierung);
        const selectedRisks = answers.riskObjectIds
          .map((id) => risks.find((r) => r.id === id))
          .filter((r): r is RiskSummary => !!r);
        const uebersichtTitle = `${tarifLabel} — Übersicht`;
        return (
          <div style={{ display: "flex", flexDirection: "column", rowGap: 18 }}>
            {isRahmen && vereinbarungMock && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: 12,
                  backgroundColor: BRAND.white,
                  border: `1px solid ${BRAND.borderDashed}`,
                  borderRadius: 12,
                  padding: "18px 20px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 8,
                  }}
                >
                  <h3
                    style={{
                      fontFamily: BRAND.fontAccent,
                      fontSize: "1.2rem",
                      color: BRAND.ink,
                      margin: 0,
                    }}
                  >
                    {uebersichtTitle}
                  </h3>
                  <span style={{ fontSize: "0.85rem", color: BRAND.muted }}>
                    {selectedRisks.length} Risiko{selectedRisks.length === 1 ? "" : "en"} im Vertrag
                  </span>
                </div>
                <SammelvertragZahlungSummary
                  vereinbarung={vereinbarungMock}
                  vehicles={selectedRisks}
                  klasseByRisk={answers.klasseByRisk}
                  assignment={answers.umfangAssignment}
                  versicherungssteuersatz={answers.sammelGrid.versicherungssteuersatz}
                />
              </div>
            )}
            <ZahlungSection
              tarifierung={answers.tarifierung}
              klasse={primaryKlasse}
              answers={answers.zahlung}
              onChange={onChangeZahlung}
            />
          </div>
        );
      }
      case "summary":
        return (
          <SummaryView
            answers={answers}
            risks={risks}
            tarifLabel={tarifLabel}
            klasseLabel={klasseLabel}
            vertragsartLabel={vertragsartLabel}
          />
        );
      default:
        return null;
    }
  };

  const stepBlocker = stepBlockerMessage(step.kind);

  return (
    <div className={styles.page}>
      <Toaster toasterId={toasterId} />
      <div className={styles.stickyHeader}>
        <div className={styles.pageHeader}>
          <Button
            appearance="subtle"
            icon={<ArrowLeft20Regular />}
            onClick={() => navigate(-1)}
          >
            Zurück
          </Button>
          <h1 className={styles.pageTitle}>
            {editContractId ? "Vertrag bearbeiten" : "Vertrag anlegen"}
            <span> · KFZ-Versicherung</span>
            {answers.vertragsart && <span> · {vertragsartLabel}</span>}
          </h1>
        </div>
        {editContractId && (
          <div
            style={{
              fontSize: "0.82rem",
              color: BRAND.muted,
              backgroundColor: "rgba(247,168,35,0.15)",
              border: `1px solid ${BRAND.gold}`,
              borderRadius: 8,
              padding: "8px 12px",
            }}
          >
            Bearbeiten-Modus: Vertrag #{editContractId}. Änderungen werden beim Speichern übernommen.
          </div>
        )}

        {(answers.contact || answers.riskObjectIds.length > 0) && (() => {
          const halterFromRisk = primaryRisk?.contacts?.[0];
          const showHalter =
            halterFromRisk &&
            answers.contact &&
            halterFromRisk.id !== answers.contact.id;
          return (
            <div className={styles.contextPanel}>
              {answers.contact && (
                <div className={styles.contextBlock}>
                  <span className={styles.contextLabel}>Kontakt:</span>
                  <span className={styles.contextName}>
                    {answers.contact.displayName}
                  </span>
                </div>
              )}
              {showHalter && (
                <div className={styles.contextBlock}>
                  <span className={styles.contextLabel}>Halter:</span>
                  <span className={styles.contextName}>
                    {halterFromRisk.displayName}
                  </span>
                </div>
              )}
              {answers.riskObjectIds.length > 0 && (
                <div className={mergeClasses(styles.contextBlock, styles.contextRisks)}>
                  <span className={styles.contextLabel}>Risikoobjekt:</span>
                  <ul className={styles.contextRiskList}>
                    {answers.riskObjectIds.map((rid) => {
                      const r = risks.find((x) => x.id === rid);
                      const plate = r?.characteristics.licensePlate;
                      return (
                        <li key={rid} className={styles.contextRiskItem}>
                          <span className={styles.contextRiskPlate}>
                            {plate || `Risiko ${rid}`}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          );
        })()}

        <div className={styles.sectionTabs} role="tablist" aria-label="Abschnitte">
          {sections.map((s, sIdx) => {
            const active = sIdx === currentSectionIdx;
            const color = sectionColorHex[s.color];
            const lockedByVereinbarung = blockDownstream && sIdx > vereinbarungIdx;
            return (
              <button
                key={s.key}
                type="button"
                role="tab"
                aria-selected={active}
                disabled={lockedByVereinbarung && !active}
                className={mergeClasses(styles.sectionTab, active && styles.sectionTabActive)}
                style={
                  active
                    ? { backgroundColor: color, borderColor: color }
                    : lockedByVereinbarung
                    ? { color: BRAND.muted, opacity: 0.45, cursor: "not-allowed" }
                    : { color }
                }
                onClick={() => {
                  if (lockedByVereinbarung) return;
                  setCurrentSectionIdx(sIdx);
                  setCurrentSubStepIdx(0);
                }}
                title={
                  lockedByVereinbarung
                    ? "Solange für diese Produkt-/Tarifierungsart keine Vereinbarung mit dem Versicherer vorliegt, sind die weiteren Abschnitte gesperrt."
                    : undefined
                }
              >
                {sIdx + 1}. {s.title}
              </button>
            );
          })}
        </div>
      </div>

      <div className={styles.formPanel}>
        <span className={styles.sectionPill} style={{ backgroundColor: accent }}>
          {section.title}
        </span>
        <div className={styles.titleRow}>
          <div style={{ display: "flex", flexDirection: "column", flexGrow: 1, minWidth: 200 }}>
            <h2 className={styles.title}>{step.title}</h2>
            {step.description && <p className={styles.description}>{step.description}</p>}
          </div>
          <div className={styles.heroBox}>
            <StepHero kind={step.kind} />
          </div>
        </div>
        <div className={styles.questions}>
          {stepBlocker ? <div className={styles.blocker}>{stepBlocker}</div> : renderStep(step.kind)}
        </div>
      </div>

      <div className={styles.stickyFooter}>
        <BottomBar
          onPrev={goPrev}
          onNext={goNext}
          onSubmit={onSubmit}
          onSaveDraft={saveDraftNow}
          canPrev={!(currentSectionIdx === 0 && currentSubStepIdx === 0)}
          canNext={!(blockDownstream && currentSectionIdx >= vereinbarungIdx)}
          nextDisabledTitle="Vereinbarung mit dem Versicherer fehlt — weitere Abschnitte sind gesperrt."
          isLastStep={isSubmitStep}
          submitting={submitting}
          savingDraft={savingDraft}
          draftSavedAt={draftSavedAt}
          progressLabel={`Schritt ${stepIndex + 1} von ${totalSteps} · ${section.title}`}
          progressFraction={progressFraction}
          submitLabel="Vertrag anlegen"
          submittingLabel="Wird angelegt…"
        />
      </div>
    </div>
  );
};

const SummaryView = ({
  answers,
  risks,
  tarifLabel,
  klasseLabel,
  vertragsartLabel,
}: {
  answers: Answers;
  risks: RiskSummary[];
  tarifLabel: string;
  klasseLabel: string;
  vertragsartLabel: string;
}) => {
  const styles = useStyles();
  const rows: Array<{ key: string; value: string }> = [];
  rows.push({ key: "Vorvertrag", value: answers.vorvertrag.exists === "ja"
    ? `Ja — ${answers.vorvertrag.gesellschaft ?? "—"} · ${answers.vorvertrag.vertragsnummer ?? "—"}`
    : answers.vorvertrag.exists === "nein"
    ? "Nein (Neuvertrag)"
    : "—" });
  rows.push({ key: "Vertragsart", value: vertragsartLabel });
  rows.push({ key: "Zugewiesenes Produkt", value: tarifLabel });
  rows.push({ key: "Primäre Fahrzeugklasse", value: klasseLabel });
  rows.push({
    key: "Risiken im Vertrag",
    value:
      risks.length === 0
        ? "—"
        : risks
            .map((r) => `${r.characteristics.licensePlate ?? `Risiko ${r.id}`}${
              r.characteristics.manufacturer
                ? ` (${r.characteristics.manufacturer} ${r.characteristics.typeName ?? ""})`.trim() + ")"
                : ""
            }`)
            .join("\n"),
  });
  const gefahrenFilled = Object.entries(answers.gefahren).filter(([, v]) => v && String(v).trim() !== "");
  rows.push({ key: "Erfasste Gefahren-/Bausteinangaben", value: `${gefahrenFilled.length} Angabe(n)` });
  const allgVertrags = VERTRAGS_SECTIONS.flatMap((s) => s.questions);
  const vertragsFilled = allgVertrags.filter((q) => (answers.vertrags[q.key] ?? "").trim() !== "");
  rows.push({
    key: "Erfasste Vertragsdaten",
    value: `${vertragsFilled.length} / ${allgVertrags.length} Felder`,
  });
  const haftpflichtPraemieKey = allgVertrags.find(
    (q) => q.frage === "Prämie brutto" && q.key.startsWith("kfz_haftpflichtversicherung")
  )?.key;
  if (haftpflichtPraemieKey && answers.vertrags[haftpflichtPraemieKey]) {
    rows.push({
      key: "Haftpflicht — Prämie brutto",
      value: `${answers.vertrags[haftpflichtPraemieKey]} €`,
    });
  }
  return (
    <div className={styles.summaryGrid}>
      {rows.map((r) => (
        <div key={r.key} className={styles.summaryRow}>
          <span className={styles.summaryKey}>{r.key}</span>
          <span className={styles.summaryValue}>{r.value || "—"}</span>
        </div>
      ))}
    </div>
  );
};

export default KfzContractCreatePage;
